import React, { useMemo } from 'react';
import useForecastMetricByWeekIdV2 from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastMetricByWeekIdV2';
import {
  useMetricByWeekIdV2,
  SplineChartV2DataPoint,
  SplineChartV2Series
} from 'src/components/BeaconV2Components/SplineChartV2';
import SplineChartV2 from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useForecastComparisonPeriod,
  useForecastComparisonStartAndEndWeekIds,
  useForecastStartAndEndWeekIds,
  useYtdStartAndEndWeekIdsForForecast
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { formatWeekIdsForAdjustmentDisplay } from 'src/utils/dateUtils';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import _get from 'lodash/get';
import { useMetricFormatter } from 'src/utils/Hooks';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  ForecastComparisonPeriod,
  ForecastPeriod
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import { weekIdToTimestamp } from 'src/components/BeaconV2Components/SplineChartV2/splineChartV2Utils';
import saveAs from 'file-saver';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  CustomTooltip,
  TooltipBox,
  TooltipIconComponent
} from 'src/components/BeaconV2Components/ExperimentalLayout/ProductGrowthPage';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useStacklineTheme } from '@stackline/ui';

/**
 * Creates a running total array from V2 data points
 */
const createRunningTotalArrayV2 = (data: SplineChartV2DataPoint[]) => {
  return data.reduce((acc, point) => {
    const lastValue = acc[acc.length - 1] || 0;
    return [...acc, lastValue + (point.value ?? 0)];
  }, [] as number[]);
};

/**
 * Creates weeks on hand spline values from V2 data format
 */
const createWeeksOnHandSplineValuesV2 = (
  unitsOnHand: SplineChartV2DataPoint[],
  unitsSold: SplineChartV2DataPoint[]
): SplineChartV2DataPoint[] => {
  return unitsOnHand.map((onHandPoint, index) => {
    const onHand = onHandPoint.value ?? 0;
    const cumulativeUnitsSold = createRunningTotalArrayV2(unitsSold.slice(index));

    // First week where units on hand is not enough to fill running total of units sold
    const weeksOnHandIndex = cumulativeUnitsSold.findIndex((i) => i >= onHand);

    // Means we can fill all units sold for every week in the data set
    if (weeksOnHandIndex === -1) {
      return {
        weekId: onHandPoint.weekId,
        value: Math.min(cumulativeUnitsSold.length, 52)
      };
    }

    const lastFullWeekIndex = Math.max(weeksOnHandIndex - 1, 0);
    const partialAvailableWeek = safeDivide(
      onHand - cumulativeUnitsSold[lastFullWeekIndex],
      unitsSold[index + weeksOnHandIndex]?.value ?? 0
    );

    const availableWeeks = Math.max(weeksOnHandIndex + partialAvailableWeek, 0);

    return {
      weekId: onHandPoint.weekId,
      value: Math.min(availableWeeks, 52)
    };
  });
};

/**
 * This chart answers the question: "How many weeks of inventory do I have on hand?"
 * We calculate this by making a running total of units sold (including forecasted units sold)
 * and then finding the week where the units on hand is less than or equal to the running total.
 */
export default function ForecastWeeksOnHandChart() {
  const theme = useStacklineTheme();
  const comparisonPeriod = useForecastComparisonPeriod();
  const isPriorPeriod = comparisonPeriod === ForecastComparisonPeriod.PRIOR_PERIOD;
  const formatMetric = useMetricFormatter();
  const { startWeekId, endWeekId } = useYtdStartAndEndWeekIdsForForecast();

  // Get comparison week IDs from the API-based helper using this chart's locked YTD range.
  const {
    startWeekId: comparisonStartWeekId,
    endWeekId: comparisonEndWeekId,
    isLoading: isLoadingComparisonWeeks
  } = useForecastComparisonStartAndEndWeekIds({
    startWeekIdOverride: startWeekId,
    endWeekIdOverride: endWeekId
  });
  const hasValidComparisonRange =
    comparisonStartWeekId > 0 && comparisonEndWeekId > 0 && comparisonStartWeekId <= comparisonEndWeekId;

  const { formattedEndDate, formattedStartDate } = formatWeekIdsForAdjustmentDisplay({ startWeekId, endWeekId });
  const { formattedEndDate: comparisonFormattedEndDate, formattedStartDate: comparisonFormattedStartDate } =
    formatWeekIdsForAdjustmentDisplay({ startWeekId: comparisonStartWeekId, endWeekId: comparisonEndWeekId });

  const { startWeekId: forecastStartWeekId, endWeekId: forecastEndWeekId } = useForecastStartAndEndWeekIds({
    forecastPeriodOverride: ForecastPeriod.FIFTY_TWO_WEEKS
  });

  // Use V2 hook for units sold with forecast data
  const {
    forecastData: forecastUnitsSold,
    primaryData: unitsSold,
    comparisonData: unitsSoldComparison,
    isLoading: unitsSoldLoading
  } = useForecastMetricByWeekIdV2({
    fieldName: 'unitsSold',
    indexName: 'sales',
    useKeyMetricForecastSummaryDataArgs: {
      buildRequest: (builder) =>
        builder.replaceConditionRangeFilter('forecastWeekId', forecastStartWeekId, forecastEndWeekId)
    },
    forecastPeriodOverride: ForecastPeriod.FULL_YEAR
  });

  /**
   * Combine units sold and forecasted units sold. Filter out duplicates because the forecast
   * model can be behind the current published week, so we may have duplicate weeks.
   */
  const combinedUnitsSold = useMemo(() => {
    const combined = [...unitsSold, ...forecastUnitsSold];
    const seen = new Set<number>();
    return combined.filter((point) => {
      if (seen.has(point.weekId)) {
        return false;
      }
      seen.add(point.weekId);
      return true;
    });
  }, [forecastUnitsSold, unitsSold]);

  // Use V2 hook for units on hand (no forecast)
  const {
    primaryData: unitsOnHand,
    comparisonData: unitsOnHandComparison,
    isLoading: unitsOnHandLoading
  } = useMetricByWeekIdV2({
    fieldName: 'unitsOnHand',
    indexName: 'sales',
    // Lock this chart's historic ranges to forecast YTD regardless of global Beacon time period.
    skipDataAlignment: true,
    primaryAggregationBuilder: (builder) => builder.replaceConditionRangeFilter('weekId', startWeekId, endWeekId),
    secondaryAggregationBuilder: (builder) =>
      hasValidComparisonRange
        ? builder.replaceConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId)
        : builder,
    requestBuilder: (builder, isComparison) => {
      if (isComparison) {
        return hasValidComparisonRange
          ? builder.replaceConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId)
          : builder;
      }
      return builder.replaceConditionRangeFilter('weekId', startWeekId, endWeekId);
    },
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch:
        startWeekId > 0 && endWeekId > 0 && hasValidComparisonRange && (isPriorPeriod || !isLoadingComparisonWeeks)
    }
  });

  const primaryWeeksOnHand = useMemo(
    () => createWeeksOnHandSplineValuesV2(unitsOnHand, combinedUnitsSold),
    [unitsOnHand, combinedUnitsSold]
  );

  const comparisonWeeksOnHand = useMemo(
    () => createWeeksOnHandSplineValuesV2(unitsOnHandComparison, [...unitsSoldComparison, ...combinedUnitsSold]),
    [unitsOnHandComparison, unitsSoldComparison, combinedUnitsSold]
  );

  const primaryLegendValue = _get(primaryWeeksOnHand, [primaryWeeksOnHand.length - 1, 'value'], 0);

  const comparisonLegendValue = useMemo(() => {
    if (isPriorPeriod) {
      // For Prior Period: use the last comparison point
      return _get(comparisonWeeksOnHand, [comparisonWeeksOnHand.length - 1, 'value'], 0);
    }
    // For Prior Year: use the same index position as the last primary point
    // (weekIds won't match since comparison is prior year, e.g., 202504 vs 202604)
    const lastPrimaryIndex = primaryWeeksOnHand.length - 1;
    return _get(comparisonWeeksOnHand, [lastPrimaryIndex, 'value'], 0);
  }, [comparisonWeeksOnHand, isPriorPeriod, primaryWeeksOnHand]);

  const percentChange = calculatePercentChange(primaryLegendValue, comparisonLegendValue);

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'weeks-on-hand-primary',
      name: 'Full Year',
      data: primaryWeeksOnHand
    }),
    [primaryWeeksOnHand]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: 'weeks-on-hand-comparison',
      name: 'Prior Year',
      data: comparisonWeeksOnHand
    }),
    [comparisonWeeksOnHand]
  );

  return (
    <BeaconChartWithLegend
      title={
        <Flex gap="sm" alignItems="center">
          <Text variant="h4">Weeks on Hand - Projected Run Rate</Text>
          <CustomTooltip
            title={<TooltipBox description="This chart only displays YTD data." />}
            stacklineTheme={theme}
            placement="right"
          >
            <TooltipIconComponent style={{ width: theme.spacing.md }} />
          </CustomTooltip>
        </Flex>
      }
      loading={unitsSoldLoading || unitsOnHandLoading}
      loadingComponent={<SplineChartLoading />}
      primaryLegendProps={{
        metric: `${formatMetric(primaryLegendValue, METRICTYPE.DECIMAL, { decimalPlaces: 2 })}`,
        change: `${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, {
          decimalPlaces: 2
        })}`,
        direction: getDirection(percentChange),
        displayName: 'Full Year'
      }}
      comparisonLegendProps={{
        metric: `${formatMetric(comparisonLegendValue, METRICTYPE.DECIMAL, { decimalPlaces: 2 })}`,
        displayName: 'Prior Year'
      }}
      convertSeriesToDelimitedData={() => {
        try {
          const data = convertLineChartSeriesToDelimitedData(
            [
              {
                type: 'spline',
                name: `${formattedStartDate} - ${formattedEndDate}`,
                data: primaryWeeksOnHand.map((point) => ({ x: weekIdToTimestamp(point.weekId), y: point.value ?? 0 }))
              },
              {
                type: 'spline',
                name: `${comparisonFormattedStartDate} - ${comparisonFormattedEndDate}`,
                data: comparisonWeeksOnHand.map((point) => ({ x: weekIdToTimestamp(point.weekId), y: point.value ?? 0 }))
              }
            ] as Series[],
            isPriorPeriod
          );
          const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
          saveAs(blob, `Weeks on Hand - Projected Run Rate.csv`);
        } catch (e) {
          console.error('error saving forecast weeks on hand - projected run rate as CSV: ', e);
        }

        return Promise.resolve();
      }}
    >
      <SplineChartV2
        metricType={METRICTYPE.DECIMAL}
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        categoryStartWeekId={startWeekId}
        categoryEndWeekId={endWeekId}
        isPriorPeriodOverride={isPriorPeriod}
      />
    </BeaconChartWithLegend>
  );
}
